import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, ArrowRight, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { AuthShell, AuthLogo, PrimaryButton, AUTH_SUBTLE, AUTH_LABEL, AUTH_BLUE } from './authShared';

const ICON = '#98A2B3';

function TeamMemberSignup({ onLogin }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('pendingInviteToken');
    if (!token) {
      toast.error('No invitation found. Please use a valid invitation link.');
      navigate('/signup');
      return;
    }
    setInviteToken(token);
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const response = await apiService.teamMemberSignup({ password: formData.password, invite_token: inviteToken });
      toast.success("You're all set — welcome to the team!");
      if (response.data.user) {
        try { localStorage.removeItem('pendingInviteToken'); } catch (e) { /* noop */ }
        onLogin(response.data.user);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Signup error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  return (
    <AuthShell backTo={null}>
      <div className="text-center mb-6">
        <AuthLogo className="mx-auto mb-4" />
        <h1 className="text-2xl">Join your team</h1>
        <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Create your account to accept the team invitation</p>
      </div>

      <div className="rounded-md border p-4 mb-6" style={{ borderColor: '#D6E4FF', background: '#EEF2FB' }}>
        <div className="flex items-start gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: AUTH_BLUE }} />
          <div className="text-sm" style={{ color: '#1D2939' }}>
            <p className="font-semibold">No payment required</p>
            <p style={{ color: AUTH_SUBTLE }}>You're being invited to join an existing team. Just create your account to get started.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password" style={{ color: AUTH_LABEL }}>Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
            <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.password} onChange={(e) => handleChange('password', e.target.value)} className={`pl-9 pr-10 h-12 ${errors.password ? 'border-red-500' : ''}`} data-testid="team-signup-password" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70" style={{ color: ICON }}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" style={{ color: AUTH_LABEL }}>Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: ICON }} />
            <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} className={`pl-9 h-12 ${errors.confirmPassword ? 'border-red-500' : ''}`} data-testid="team-signup-confirm-password" />
          </div>
          {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
        </div>
        <PrimaryButton type="submit" disabled={isLoading} data-testid="team-signup-submit" className="flex items-center justify-center gap-2">
          {isLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Creating Account...</>) : (<>Create Account & Join Team <ArrowRight className="w-4 h-4" /></>)}
        </PrimaryButton>
      </form>

      <p className="text-center text-sm mt-6" style={{ color: AUTH_SUBTLE }}>
        Already have an account?{' '}
        <Link to="/login" className="font-semibold hover:opacity-70" style={{ color: AUTH_BLUE }}>Sign in instead</Link>
      </p>
    </AuthShell>
  );
}

export default TeamMemberSignup;
